import React, { useState, useEffect } from 'react';
import PhoneInput from './utils/PhoneInput';
import OtpInput from './utils/OtpInput';
import { validatePhone, validateOtp } from './utils/validate';
import { authAPI } from '../../../services/api';

import { useTranslation } from "react-i18next";

import LanguageSelect from './../../Common/Localization/LanguageSelect'

const Login = ({ onRequestOtp, onLogin, onSwitchToRegister, loading = false }) => {
  const { t } = useTranslation("login");
  const { t: vt } = useTranslation("validate"); // для валидаций
  
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    phone: '',
    otp: ''
  });
  const [errors, setErrors] = useState({});
  const [triesLeft, setTriesLeft] = useState(null);
  
  // ✅ Состояния для соглашения
  const [agreementData, setAgreementData] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementLoading, setAgreementLoading] = useState(true);

  // ✅ Загружаем соглашение при монтировании компонента
  useEffect(() => {
    const fetchAgreement = async () => {
      try {
        setAgreementLoading(true);
        const response = await authAPI.getCurrentAgreement();
        console.log("Получено соглашение в Login:", response.data);
        if (response.data) {
          setAgreementData(response.data);
        }
      } catch (error) {
        console.error("Ошибка получения соглашения в Login:", error);
        console.log("Используем дефолтные данные");
        setErrors({ general: error?.response?.data?.error })
      } finally {
        setAgreementLoading(false);
      }
    };

    fetchAgreement();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    document.activeElement?.blur();

    const phoneErrors = validatePhone(formData.phone, vt);
    if (Object.keys(phoneErrors).length > 0) {
      setErrors(phoneErrors);
    }

    try{
      const dataToSend = {
        ...formData,
        phone: formData.phone.replace(/\D/g, '') || ''
      };
      const success = await onRequestOtp?.(dataToSend);
  
      if (success === true) {
        setStep(2);
        setTriesLeft(null);
      } else {
        setErrors({ phone: t("SMS_SEND_ERR") });
      }
    } catch(e){
      setErrors({ phone: t("SMS_SEND_ERR") });
    }
    
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    const otpErrors = validateOtp(formData.otp, vt);
    if (Object.keys(otpErrors).length > 0) {
      setErrors(otpErrors);
      return;
    }

    const dataToSend = {
      phone: formData.phone.replace(/\D/g, ''),
      otp: formData.otp.replace(/\D/g, '')
    };

    if (onLogin) {
      const result = await onLogin(dataToSend);
      
      // ✅ Проверяем различные типы ошибок
      if (result) {
        // Проверка на "user not found!"
        if (result.error === "user not found!") {
          setErrors({ 
            otp: t("USER_NOT_FOUND")
          });
          return;
        }
        
        // Проверка на неправильный OTP
        if (result.is_logined === false && result.tries_available !== undefined) {
          const tries = result.tries_available;
          setTriesLeft(tries);
          
          if (tries > 0) {
            setErrors({ 
              otp: `${t("CODE_LEFT_ERR")} ${tries}` 
            });
          } else {
            setErrors({ 
              otp: t("ASK_NEW_CODE_ERR") 
            });
          }
          return;
        }
        
        // Проверка на другие ошибки
        if (result.error && result.error !== "user not found!") {
          setErrors({ 
            otp: result.error 
          });
          return;
        }
      }
    }
  };

  const handleBackToPhone = () => {
    setStep(1);
    setErrors({});
    setTriesLeft(null);
    setFormData(prev => ({ ...prev, otp: '' }));
  };

  const handleRequestNewCode = async () => {
    const dataToSend = {
      phone: formData.phone.replace(/\D/g, '')
    };

    const success = await onRequestOtp?.(dataToSend);
    
    if (success !== false) {
      setErrors({});
      setTriesLeft(null);
      setFormData(prev => ({ ...prev, otp: '' }));
    } else {
      setErrors({ otp: t("NEW_CODE_SEND_ERR") });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-2 py-12">
        <div className="w-full max-w-md mx-auto bg-white rounded-2xl p-8 shadow-xl">
          
          {/* Заголовок */}

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              {step === 1 ? t("ENTER_PHONE") : t("ENTER_SMS_MSG")}
            </h1>
            <p className="text-gray-500 text-base">
              {step === 1 ? t("FOR_ENTER") : t("WE_SENDED_CODE")}
            </p>
          </div>

          {/* Форма */}
          <div className="flex flex-col gap-6">
            {step === 1 ? (
              <PhoneInput
                value={formData.phone}
                onChange={(value) => handleInputChange('phone', value)}
                error={errors.phone}
                disabled={loading}
              />
            ) : (
              <OtpInput
                value={formData.otp}
                onChange={(value) => handleInputChange('otp', value)}
                error={errors.otp}
                disabled={loading}
              />
            )}

            <button
              onClick={step === 1 ? handlePhoneSubmit : handleOtpSubmit}
              onTouchEnd={step === 1 ? handlePhoneSubmit : handleOtpSubmit}
              disabled={loading || (step === 2 && triesLeft === 0)}
              className="w-full bg-blue-500 text-white py-4 rounded-xl font-semibold text-lg transition 
                         hover:bg-blue-600 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin"></div>
                  {step === 1 ? t("SENDING") : t("CHECKING")}
                </div>
              ) : (
                step === 1 ? t("CONTINUE") : t("ACCEPT")
              )}
            </button>

            {/* Кнопки для второго шага */}
            {step === 2 && (
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleBackToPhone}
                  disabled={loading}
                  className="w-full text-blue-500 font-medium py-3 hover:text-blue-600 disabled:opacity-60"
                >
                  {"←" + t("CHANGE_PHONE")}
                </button>
                
                {/* Кнопка запроса нового кода, если попытки закончились */}
                {triesLeft === 0 && (
                  <button
                    onClick={handleRequestNewCode}
                    disabled={loading}
                    className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-60"
                  >
                    {t("ASK_NEW_CODE")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Ссылка на регистрацию */}
          {step === 1 && onSwitchToRegister && (
            <div className="text-center text-gray-500 mt-6">
              <span>{t("NO_ACCOUNT")}</span>
              <button
                onClick={onSwitchToRegister}
                disabled={loading}
                className="ml-2 text-blue-500 font-medium hover:text-blue-600 disabled:opacity-70"
              >
                {t("SIGN_UP")}
              </button>
            </div>
          )}

          {/* Соглашение */}
          {step === 1 && (
            <div className="text-center mt-4">
              <p className="text-gray-500 text-sm">
                {t("YOU_AGREE_WITH")}{' '}
                <button
                  type="button"
                  onClick={() => setShowAgreementModal(true)}
                  className="text-blue-500 font-medium hover:text-blue-600 underline"
                  disabled={agreementLoading}
                >
                  {t("USERS_EULA")}
                </button>
              </p>
            </div>
          )}
          {step === 1 && (
            <div className="mt-4">
                <LanguageSelect />
            </div>
          )}
        </div>
      </div>
      
      {/* ✅ Модалка соглашения */}
      {showAgreementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto mx-4">
            <h2 className="text-xl font-bold mb-4">
              {agreementData?.name || t("USER_EULA")}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {t("DATE")}: {agreementData?.date || "—"}
            </p>
            <div
              className="text-gray-700 prose max-w-none"
              dangerouslySetInnerHTML={{
                __html:
                  agreementData?.content ||
                  "<p>" + t("EULA_NO_ACCESS") + "</p>",
              }}
            />
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAgreementModal(false)}
                className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
              >
                {t("CLOSE")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;